// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyClient } from "@/types";
import ClientCard from "@/UI/cards/ClientCard";
import ActionsBar, { type TyBarTab } from "@/UI/components/ActionsBar";
import ListPanel from "@/UI/components/ListPanel";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ClientFormPopup from "@/UI/popups/ClientFormPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import PasswordPopup from "@/UI/popups/PasswordPopup";
// --- Logic --------------------------------------------------------------------------------------
type TyTab = "all" | "owners" | "multi" | "temporary" | "suspended";

/** What each scope asks the endpoint for. A tab is a different list, not a narrowing of one. */
const TAB_QUERY: Record<TyTab, Record<string, string>> = {
    all: {},
    owners: { owners: "true" },
    multi: { multi: "true" },
    temporary: { isPasswordTemporary: "true" },
    suspended: { isActive: "false" },
};

/**
 * Everyone who works in any company on Lozicore. **Cross-tenant, and that is the point**: a person is
 * one record with one phone number however many companies they belong to, so this is the only list in
 * the product that can show somebody who is an owner in one and a member in two others.
 *
 * It is a support screen rather than a directory. The reason to open it is that somebody cannot sign
 * in, so **there is no create**: staff are added by their own company, on its team screen. What this
 * side can do is the thing nobody else can — issue a new password, which is the whole of this
 * product's reset story, and switch an account off.
 */
export default function ClientsScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const tab = (params.get("tab") as TyTab) || "all";
    const search = params.get("search") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);
    /** Who the password in flight belongs to. The response carries the secret, never the name. */
    const [issuedFor, setIssuedFor] = useState("");

    const clients = useFetch<{ clients: TyClient[]; total: number }>("/clients", {}, false);
    /** Row actions share one call: only one can be in flight, and each sets its own path. */
    const action = useFetch("/clients", {}, false);

    const load = () => {
        clients.invoke({ query: { ...TAB_QUERY[tab], search: search.trim() || undefined, page, limit: pageSize } });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, search, page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    /** A password only ever exists in the response that created it, so it is shown the moment it lands. */
    useEffect(() => {
        const password = (action.data as { password?: string } | null)?.password;
        if (!action.succeededAt || !password) return;
        addComp({ name: "password", Component: PasswordPopup, anim: "scale", details: { name: issuedFor, password } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        // Narrowing a list — or changing tab — puts you back at its start.
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const rows = clients.data?.clients ?? [];
    const tabs: TyBarTab[] = (Object.keys(TAB_QUERY) as TyTab[]).map((key) => ({ key, label: t.clients.tabs[key] }));

    const openPassword = (client: TyClient) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.password.issue,
                message: t.password.confirmHint.replace("{name}", client.name),
                confirmLabel: t.password.issue,
                danger: false,
                onConfirm: () => {
                    setIssuedFor(client.name);
                    action.invoke({ method: "POST", path: `/clients/${client._id}/password` });
                },
            },
        });

    const openActions = (client: TyClient, anchor: DOMRect) => {
        // No "open" entry: the whole card already does that, and offering it again took the first
        // row of every menu to repeat the one thing the card is for.
        const actions: TyAction[] = [
            {
                key: "edit",
                label: t.common.edit,
                icon: "edit",
                onSelect: () => addComp({ name: "client-form", Component: ClientFormPopup, anim: "scale", details: { client, onDone: load } }),
            },
            {
                key: "active",
                label: client.isActive ? t.clients.suspend : t.clients.activate,
                icon: "block",
                danger: client.isActive,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: client.isActive ? t.clients.suspend : t.clients.activate,
                            message: (client.isActive ? t.clients.confirmSuspend : t.clients.confirmActivate).replace("{name}", client.name),
                            confirmLabel: client.isActive ? t.clients.suspend : t.clients.activate,
                            danger: client.isActive,
                            onConfirm: () => action.invoke({ method: "PATCH", path: `/clients/${client._id}`, data: { isActive: !client.isActive } }),
                        },
                    }),
            },
        ];

        addComp({
            name: "client-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    return (
        <section className="list-screen">
            <ListPanel title={t.clients.title} subtitle={t.clients.subtitle.replace("{count}", String(clients.data?.total ?? 0))}>
                <ActionsBar
                    tabs={tabs}
                    activeTab={tab}
                    onTab={(key) => apply({ tab: key === "all" ? null : key })}
                    isFilterOpen={isFiltering}
                    onFilterOpen={() => {
                        setDraftSearch(search);
                        setIsFiltering(true);
                    }}
                    hasFilters={!!search}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        apply({ search: draftSearch.trim() || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setIsFiltering(false);
                        apply({ search: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        <FilterSearchInput
                            value={draftSearch}
                            onChange={setDraftSearch}
                            label={t.common.search}
                            placeholder={t.clients.searchPlaceholder}
                            onEnter={() => {
                                setIsFiltering(false);
                                apply({ search: draftSearch.trim() || null });
                            }}
                        />
                    }
                />
            </ListPanel>

            {action.error && <p className="screen-state screen-state--error">{action.error}</p>}
            {clients.loading && <p className="screen-state">{t.common.loading}</p>}
            {clients.error && <p className="screen-state screen-state--error">{clients.error}</p>}
            {!clients.loading && !clients.error && !rows.length && <p className="screen-state">{t.clients.empty}</p>}

            <div className="list-screen__rows">
                {rows.map((client) => (
                    <ClientCard
                        key={client._id}
                        client={client}
                        onOpen={() => navigate(`/clients/${client._id}`)}
                        onPassword={() => openPassword(client)}
                        onActions={(anchor) => openActions(client, anchor)}
                    />
                ))}
            </div>

            <Paginator
                page={page}
                total={clients.data?.total ?? 0}
                count={rows.length}
                onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
            />
        </section>
    );
}
